import queryString from 'query-string'
import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router'
import { useToggle } from 'react-use'

import agent from 'agent'
import { AccountNameConflictForm } from 'components/authentication/registration/self-service/AccountNameConflictForm'
import { AccountSetupForm } from 'components/authentication/registration/self-service/AccountSetupForm'
import { SignedOutForm } from 'components/authentication/registration/self-service/SignedOutForm'
import SignedOutFormContainer from 'components/authentication/SignedOutFormContainer'
import { NotFound } from 'components/NotFound'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { AccountSetupFormProps } from 'types/authentication/registration/self-service/account-setup'
import { messageFromResponseError } from 'utils/strings'

export const AccountSetup: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const authState = useAuth()

  const {
    first_name: firstName,
    last_name: lastName,
    company_name: initialCompanyName,
    email,
    code
  } = queryString.parse(location?.search)

  const [isActivating, setIsActivating] = useToggle(false)
  const [hasNameConflict, setHasNameConflict] = useToggle(false)

  const handleSignUp = useCallback(
    async (formData) => {
      const { allowUsStorage, companyName, password, teamName } = formData

      setIsActivating(true)
      setHasNameConflict(false)

      try {
        const response = await agent
          .post('/companies', {
            code,
            admin_email: email,
            admin_password: password,
            company_name: companyName,
            team_name: teamName,
            allow_us_storage: allowUsStorage
          })
          .then(({ data }) => data)

        await authState.startSession(response.session)

        setIsActivating(false)
        history.push('/getting-started')
      } catch (err) {
        if (err.response && err.response.status === 409) {
          setHasNameConflict(true)
        } else {
          toast({
            type: 'error',
            time: 6000,
            text: messageFromResponseError(
              err,
              'Unable to sign up. Please try again later.'
            )
          })
        }
        setIsActivating(false)
      }
    },
    [authState, code, email, history, setHasNameConflict, setIsActivating]
  )

  const methods = useForm<AccountSetupFormProps>({
    mode: 'all',
    defaultValues: {
      password: '',
      confirmPassword: '',
      companyName: initialCompanyName || '',
      teamName: '',
      allowUsStorage: false
    }
  })

  const handleCompanyNameChange = (name) => {
    methods.setValue('companyName', name)
    methods.handleSubmit(handleSignUp)()
  }

  if (!code || !email) return <NotFound />

  return (
    <SignedOutFormContainer>
      <SignedOutForm
        methods={methods}
        title="Account setup"
        onSubmit={methods.handleSubmit(handleSignUp)}
      >
        {hasNameConflict ? (
          <AccountNameConflictForm
            companyName={methods.watch('companyName')}
            disabled={isActivating}
            handleClearConflict={() => setHasNameConflict(false)}
            handleCompanyNameChange={handleCompanyNameChange}
          />
        ) : (
          <AccountSetupForm
            disabled={isActivating}
            email={email}
            firstName={firstName}
            lastName={lastName}
          />
        )}
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}
